import React from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/Collapsible";
import Checkbox from "../ui/Checkbox";
import DragNumberInput from "../ui/DragNumberInput";
import FieldLabel from "../ui/FieldLabel";
import MenuSelect from "../ui/MenuSelect";
import Separator from "../ui/Separator";
import { useBodies, useLights, useSceneActions, useSites } from "../../stores/sceneStore";
import { useSelectionStore } from "../../stores/selectionStore";
import { getTransformEditorBlockProps, usePhysicsAuthoringLock } from "../../utils/physicsGuards";
import InspectorPhysicsLockedCollapsibleWrap from "./InspectorPhysicsLockedCollapsibleWrap";

const EMPTY_OBJECT = Object.freeze({});
const LIGHT_TYPES = ["spot", "directional", "point"];
const NONE_VALUE = "__none__";
const inputClass =
  "w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-gray-100 text-xs outline-none focus:border-gray-500 disabled:cursor-not-allowed disabled:opacity-60";

const sortByName = (a, b) => String(a?.name || "").localeCompare(String(b?.name || ""));

const numericValue = (value, fallback = 0) => {
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
};

function Field({ label, children }) {
  return (
    <label className="space-y-1">
      <FieldLabel className="text-[10px] uppercase">{label}</FieldLabel>
      {children}
    </label>
  );
}

function NumberField({ label, value, onChange, disabled, min, max, step = "0.1" }) {
  const numeric = Number.isFinite(Number(value)) ? Number(value) : 0;
  const snap = Number(step) || 0.1;
  return (
    <DragNumberInput
      label={label}
      value={numeric}
      dragValue={numeric}
      onChange={(event) => onChange(numericValue(event.target.value, numeric))}
      onDragValueChange={onChange}
      wrapperClassName="space-y-1"
      labelClassName="text-[10px] panel-text-helper uppercase"
      inputClassName={inputClass}
      dragSensitivity={snap}
      dragSnapStep={snap}
      inputProps={{ disabled, min, max, step }}
    />
  );
}

function VectorField({ label, value, onChange, disabled, step = "0.1" }) {
  const vec = Array.isArray(value) && value.length >= 3 ? value : [0, 0, 0];
  const updateAt = (index, raw) => {
    const next = [...vec];
    next[index] = numericValue(raw, vec[index]);
    onChange(next);
  };
  return (
    <Field label={label}>
      <div className="grid grid-cols-3 gap-2">
        {vec.map((component, index) => (
          <input
            key={index}
            className={inputClass}
            type="number"
            value={Number.isFinite(Number(component)) ? component : 0}
            step={step}
            disabled={disabled}
            onChange={(event) => updateAt(index, event.target.value)}
            aria-label={`${label} ${["x", "y", "z"][index]}`}
          />
        ))}
      </div>
    </Field>
  );
}

function CheckboxField({ label, checked, onChange, disabled }) {
  return (
    <Checkbox
      label={label}
      checked={Boolean(checked)}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
      className="border border-gray-700"
    />
  );
}

function selectOptionsFromRecords(records) {
  return Object.values(records || EMPTY_OBJECT)
    .filter(Boolean)
    .sort(sortByName)
    .map((item) => ({ value: item.uuid, label: item.name || item.uuid }));
}

export default function LightPropertiesPanel() {
  const selectedLight = useSelectionStore((state) => state.selectedLight);
  const lights = useLights() ?? EMPTY_OBJECT;
  const bodies = useBodies() ?? EMPTY_OBJECT;
  const sites = useSites() ?? EMPTY_OBJECT;
  const sceneActions = useSceneActions();
  const authoringLocked = usePhysicsAuthoringLock();
  const editorBlock = getTransformEditorBlockProps(authoringLocked);
  const disabled = editorBlock.disabled;

  const light = selectedLight ? lights[selectedLight] : null;
  const bodyOptions = React.useMemo(() => selectOptionsFromRecords(bodies), [bodies]);
  const siteOptions = React.useMemo(() => selectOptionsFromRecords(sites), [sites]);

  if (!light || !selectedLight) return null;

  const update = (patch) => sceneActions.updateLight(selectedLight, patch);
  const setMountKind = (kind) => {
    if (kind === "fixed") {
      update({
        kind: "fixed",
        mountType: undefined,
        mountBodyUuid: undefined,
        mountSiteUuid: undefined,
      });
      return;
    }
    update({
      kind: "mounted",
      mountType: "body",
      mountBodyUuid: bodyOptions[0]?.value,
      mountSiteUuid: undefined,
    });
  };

  const mountOptions = light.mountType === "site" ? siteOptions : bodyOptions;
  const selectedMountUuid = light.mountType === "site" ? light.mountSiteUuid : light.mountBodyUuid;

  return (
    <div className="p-3 space-y-4">
      <InspectorPhysicsLockedCollapsibleWrap locked={authoringLocked}>
        <Collapsible defaultOpen>
          <CollapsibleTrigger quiet aria-label="Toggle Light Settings">
            <span className="min-w-0 truncate text-left">Light Settings</span>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="pt-2 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <FieldLabel as="label" className="block mb-1">
                    Type
                  </FieldLabel>
                  <MenuSelect
                    size="compact"
                    value={light.type || "spot"}
                    disabled={disabled}
                    onChange={(type) => update({ type })}
                    options={LIGHT_TYPES.map((type) => ({ value: type, label: type }))}
                    triggerProps={{ onPointerDown: (event) => event.stopPropagation() }}
                    aria-label="Light type"
                  />
                </div>
                <div>
                  <FieldLabel as="label" className="block mb-1">
                    Mount
                  </FieldLabel>
                  <MenuSelect
                    size="compact"
                    value={light.kind || "fixed"}
                    disabled={disabled}
                    onChange={setMountKind}
                    options={[
                      { value: "fixed", label: "fixed" },
                      { value: "mounted", label: "mounted" },
                    ]}
                    triggerProps={{ onPointerDown: (event) => event.stopPropagation() }}
                    aria-label="Light mount mode"
                  />
                </div>
              </div>

              {light.kind === "mounted" ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <FieldLabel as="label" className="block mb-1">
                      Mounted To
                    </FieldLabel>
                    <MenuSelect
                      size="compact"
                      value={light.mountType || "body"}
                      disabled={disabled}
                      onChange={(mountType) =>
                        update({
                          mountType,
                          mountBodyUuid: mountType === "body" ? bodyOptions[0]?.value : undefined,
                          mountSiteUuid: mountType === "site" ? siteOptions[0]?.value : undefined,
                        })
                      }
                      options={[
                        { value: "body", label: "body" },
                        { value: "site", label: "site" },
                      ]}
                      triggerProps={{ onPointerDown: (event) => event.stopPropagation() }}
                      aria-label="Light mount target type"
                    />
                  </div>
                  <div>
                    <FieldLabel as="label" className="block mb-1">
                      {light.mountType === "site" ? "Site" : "Body"}
                    </FieldLabel>
                    <MenuSelect
                      size="compact"
                      value={selectedMountUuid || ""}
                      disabled={disabled}
                      onChange={(uuid) =>
                        update(
                          light.mountType === "site"
                            ? { mountSiteUuid: uuid, mountBodyUuid: undefined }
                            : { mountBodyUuid: uuid, mountSiteUuid: undefined }
                        )
                      }
                      options={[
                        { value: "", label: "Select..." },
                        ...mountOptions.map((option) => ({
                          value: option.value,
                          label: option.label,
                        })),
                      ]}
                      contentClassName="max-h-[min(240px,50vh)]"
                      triggerProps={{ onPointerDown: (event) => event.stopPropagation() }}
                      aria-label="Light mount target"
                    />
                  </div>
                </div>
              ) : null}

              <div className="grid grid-cols-2 gap-3">
                <CheckboxField
                  label="Active"
                  checked={light.active}
                  disabled={disabled}
                  onChange={(active) => update({ active })}
                />
                <CheckboxField
                  label="Cast Shadow"
                  checked={light.castshadow}
                  disabled={disabled}
                  onChange={(castshadow) => update({ castshadow })}
                />
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </InspectorPhysicsLockedCollapsibleWrap>

      <Separator />

      <InspectorPhysicsLockedCollapsibleWrap locked={authoringLocked}>
        <Collapsible defaultOpen>
          <CollapsibleTrigger quiet aria-label="Toggle Transform">
            <span className="min-w-0 truncate text-left">Transform</span>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="pt-2 space-y-4">
              <VectorField
                label="Position"
                value={light.position}
                disabled={disabled}
                onChange={(position) => update({ position })}
              />
              <VectorField
                label="Direction"
                value={light.direction}
                disabled={disabled}
                onChange={(direction) => update({ direction })}
              />
            </div>
          </CollapsibleContent>
        </Collapsible>
      </InspectorPhysicsLockedCollapsibleWrap>

      <Separator />

      <InspectorPhysicsLockedCollapsibleWrap locked={authoringLocked}>
        <Collapsible defaultOpen>
          <CollapsibleTrigger quiet aria-label="Toggle MuJoCo Light Properties">
            <span className="min-w-0 truncate text-left">MuJoCo Light Properties</span>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="pt-2 space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <NumberField
                  label="Range"
                  value={light.range}
                  min={0}
                  disabled={disabled}
                  onChange={(range) => update({ range })}
                />
                <NumberField
                  label="Cutoff"
                  value={light.cutoff}
                  min={0}
                  max={180}
                  disabled={disabled}
                  onChange={(cutoff) => update({ cutoff })}
                />
                <NumberField
                  label="Exponent"
                  value={light.exponent}
                  min={0}
                  disabled={disabled}
                  onChange={(exponent) => update({ exponent })}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <NumberField
                  label="Intensity"
                  value={light.intensity ?? 1}
                  min={0}
                  disabled={disabled}
                  onChange={(intensity) => update({ intensity })}
                />
                <NumberField
                  label="Bulb Radius"
                  value={light.bulbradius ?? 0}
                  min={0}
                  disabled={disabled}
                  onChange={(bulbradius) => update({ bulbradius })}
                />
              </div>

              <VectorField
                label="Diffuse"
                value={light.diffuse}
                step="0.05"
                disabled={disabled}
                onChange={(diffuse) => update({ diffuse })}
              />
              <VectorField
                label="Ambient"
                value={light.ambient}
                step="0.05"
                disabled={disabled}
                onChange={(ambient) => update({ ambient })}
              />
              <VectorField
                label="Specular"
                value={light.specular}
                step="0.05"
                disabled={disabled}
                onChange={(specular) => update({ specular })}
              />
              <VectorField
                label="Attenuation"
                value={light.attenuation}
                step="0.05"
                disabled={disabled}
                onChange={(attenuation) => update({ attenuation })}
              />

              <div>
                <FieldLabel as="label" className="block mb-1">
                  Target Body
                </FieldLabel>
                <MenuSelect
                  size="compact"
                  value={light.targetBodyUuid || NONE_VALUE}
                  disabled={disabled}
                  onChange={(targetBodyUuid) =>
                    update({
                      targetBodyUuid: targetBodyUuid === NONE_VALUE ? undefined : targetBodyUuid,
                      trackingMode: targetBodyUuid === NONE_VALUE ? undefined : "targetbody",
                    })
                  }
                  options={[
                    { value: NONE_VALUE, label: "None" },
                    ...bodyOptions.map((option) => ({ value: option.value, label: option.label })),
                  ]}
                  contentClassName="max-h-[min(240px,50vh)]"
                  triggerProps={{ onPointerDown: (event) => event.stopPropagation() }}
                  aria-label="Light target body"
                />
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </InspectorPhysicsLockedCollapsibleWrap>
    </div>
  );
}
